import { Link, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import QuickCallbackForm from "@/components/QuickCallbackForm";
import { COMMERCIAL_PAGE_MAP } from "@/data/commercial-pages";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { track } from "@/lib/track";
import { useUI } from "@/context/UIContext";
import { ArrowRight, ArrowUpRight, Check, MapPin, Building2, AlertTriangle, Clock, Star } from "lucide-react";
import { CONTACT } from "@/data/site";

const STEPS = [
  { num: "01", title: "Vous déposez votre brief", text: "Deux minutes : type d'événement, ville, date, nombre de convives, budget estimatif." },
  { num: "02", title: "Échange de cadrage", text: "Sous 24 h ouvrées, votre chef de projet vous rappelle pour cadrer le projet." },
  { num: "03", title: "3 pistes chiffrées", text: "Vous recevez trois premières pistes avec estimation budgétaire, sans engagement." },
  { num: "04", title: "Nous livrons le jour J", text: "Votre interlocuteur unique coordonne tout sur place. Vous accueillez vos invités." },
];

export default function CommercialPage() {
  const { pathname } = useLocation();
  const { setCallbackOpen } = useUI();
  const p = COMMERCIAL_PAGE_MAP[pathname.replace(/^\//, "")];
  if (!p) return <Navigate to="/" replace />;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: p.h1,
    description: p.seoDescription,
    provider: {
      "@type": "LocalBusiness",
      name: "Désir Events",
      url: "https://desir-events.com",
      telephone: CONTACT.phoneFixe,
      email: CONTACT.email,
      address: { "@type": "PostalAddress", streetAddress: "129 rue de Servient", addressLocality: "Lyon", postalCode: "69003", addressCountry: "FR" },
    },
    areaServed: { "@type": "Country", name: "France" },
  };
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <>
      <Seo title={p.seoTitle} description={p.seoDescription} path={`/${p.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://desir-events.com/" },
            { "@type": "ListItem", position: 2, name: p.eyebrow },
            { "@type": "ListItem", position: 3, name: p.h1, item: `https://desir-events.com/${p.slug}` },
          ],
        })}</script>
      </Helmet>
      <PageHero eyebrow={p.eyebrow} title={p.h1} />

      <nav className="mx-auto max-w-[1440px] px-5 pt-6 md:px-10" aria-label="Fil d'Ariane" data-testid="cp-breadcrumb">
        <ol className="flex flex-wrap items-center gap-2 text-xs text-ink/45">
          <li><Link to="/" className="hover:text-lime-dim">Accueil</Link></li>
          <li aria-hidden="true">/</li>
          <li>{p.eyebrow}</li>
          <li aria-hidden="true">/</li>
          <li className="text-ink/70" aria-current="page">{p.h1}</li>
        </ol>
      </nav>

      {/* Promesse + CTA */}
      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24" data-testid="cp-intro">
        <div className="grid items-start gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal><span className={overline}>Notre promesse</span></Reveal>
            <Reveal delay={1}><p className="mt-5 max-w-2xl font-serif text-2xl leading-snug text-ink md:text-3xl">{p.promesse}</p></Reveal>
            {p.intro && (
              <Reveal delay={2}>
                <div className="mt-6 max-w-2xl space-y-4" data-testid="cp-intro-text">
                  {p.intro.map((t) => <p key={t.slice(0, 30)} className="text-base leading-relaxed text-ink/65">{t}</p>)}
                </div>
              </Reveal>
            )}
            <Reveal delay={2}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="cp-devis-btn">Recevoir une première proposition <ArrowRight size={16} /></Link>
                <button onClick={() => { track("click_rappel"); setCallbackOpen(true); }} className={btnOutline} data-testid="cp-callback-btn">Être rappelé</button>
              </div>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-5 flex items-center gap-2 text-sm text-ink/55"><Clock size={15} className="text-lime-dim" /> Réponse sous 24 h ouvrées · sans engagement</p>
            </Reveal>
          </div>
          <Reveal delay={1} className="md:col-span-5">
            <img src={p.image} alt={p.imageAlt} className="aspect-[4/3] w-full rounded-2xl object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Contexte local */}
      <section className="border-t border-ink/10 bg-mist" data-testid="cp-local">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Notre terrain</span></Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            <Reveal>
              <div className="h-full rounded-2xl border border-ink/10 bg-white p-8">
                <Building2 size={20} className="text-lime-dim" />
                <h2 className="mt-4 font-serif text-2xl text-ink">Le tissu économique</h2>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{p.secteurs}</p>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <div className="h-full rounded-2xl border border-ink/10 bg-white p-8">
                <MapPin size={20} className="text-lime-dim" />
                <h2 className="mt-4 font-serif text-2xl text-ink">Les lieux que nous connaissons</h2>
                <ul className="mt-3 space-y-2">
                  {p.lieux.map((l) => (
                    <li key={l} className="flex items-start gap-2 text-sm text-ink/60"><Check size={14} className="mt-0.5 shrink-0 text-lime-dim" />{l}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={2}>
              <div className="h-full rounded-2xl border border-ink/10 bg-white p-8">
                <AlertTriangle size={20} className="text-lime-dim" />
                <h2 className="mt-4 font-serif text-2xl text-ink">Les contraintes que nous anticipons</h2>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{p.contraintes}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Différenciation — affichée uniquement si renseignée */}
      {p.whyUs && (
        <section className="border-t border-ink/10 bg-white" data-testid="cp-why-us">
          <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
            <Reveal><span className={overline}>Notre différence</span></Reveal>
            <Reveal delay={1}><h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">{p.whyUs.title}</h2></Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {p.whyUs.points.map((w, i) => (
                <Reveal key={w.title} delay={i}>
                  <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7">
                    <Star size={18} className="text-lime-dim" />
                    <h3 className="mt-4 font-serif text-xl text-ink">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink/60">{w.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Formulaire court « Être rappelé » — affiché uniquement si demandé */}
      {p.quickForm && (
        <section className="border-t border-ink/10 bg-ink text-white" data-testid="cp-quick-form">
          <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-16 md:grid-cols-2 md:px-10 md:py-24">
            <div>
              <Reveal><span className={`${overline} !text-lime`}>Vous préférez le téléphone ?</span></Reveal>
              <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">Un chef de projet vous rappelle.</h2></Reveal>
              <Reveal delay={2}><p className="mt-5 max-w-md text-base leading-relaxed text-white/60">Laissez quatre informations — nous rappelons sous 24 h ouvrées pour cadrer votre événement. Le brief complet reste disponible pour les projets déjà détaillés.</p></Reveal>
            </div>
            <Reveal delay={1}><QuickCallbackForm testid="cp-quick-callback" /></Reveal>
          </div>
        </section>
      )}

      {/* Organisation locale — affiché uniquement si renseigné */}
      {p.organisation && (
        <section className="border-t border-ink/10 bg-white" data-testid="cp-organisation">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
            <Reveal><span className={overline}>Notre organisation sur le territoire</span></Reveal>
            <Reveal delay={1}><p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/70">{p.organisation}</p></Reveal>
          </div>
        </section>
      )}

      {/* Prestations + Exemple */}
      <section className="border-t border-ink/10 bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-6">
            <Reveal><span className={overline}>Prestations incluses</span></Reveal>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {p.prestations.map((pr, i) => (
                <Reveal key={pr} delay={i % 4}>
                  <li className="flex items-center gap-3 rounded-xl border border-ink/10 bg-mist px-5 py-4 text-sm text-ink/80">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Check size={13} /></span>{pr}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="md:col-span-6">
            <Reveal><span className={overline}>Exemple de réalisation</span></Reveal>
            <Reveal delay={1}>
              <Link to={p.exemple.to} className="group mt-8 block rounded-2xl border border-ink/10 bg-ink p-8 text-white transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl" data-testid="cp-exemple">
                <h3 className="font-serif text-2xl leading-snug group-hover:text-lime">{p.exemple.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/65">{p.exemple.text}</p>
                <span className="mt-6 flex items-center gap-2 text-sm text-lime">Voir nos réalisations <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Sections éditoriales additionnelles (H2) — affichées uniquement si renseignées */}
      {p.extraSections && (
        <section className="border-t border-ink/10 bg-white" data-testid="cp-extra-sections">
          <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
            <div className="grid gap-10 md:grid-cols-2">
              {p.extraSections.map((s, i) => (
                <Reveal key={s.h2} delay={i % 2}>
                  <div>
                    <h2 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">{s.h2}</h2>
                    <p className="mt-4 text-base leading-relaxed text-ink/65">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Déroulement */}
      <section className="border-t border-white/10 bg-ink text-white" data-testid="cp-steps">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={`${overline} !text-lime`}>Comment ça marche</span></Reveal>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-white/10 md:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i}>
                <div className="flex h-full flex-col bg-ink p-8">
                  <span className="font-serif text-4xl text-lime">{s.num}</span>
                  <h3 className="mt-5 font-serif text-xl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Budget : 3 niveaux de format si renseigné, sinon repères classiques */}
      {p.pricing ? (
        <section className="border-t border-ink/10 bg-mist" data-testid="cp-pricing">
          <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
            <Reveal><span className={overline}>Budget</span></Reveal>
            <Reveal delay={1}><h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">{p.pricing.title}</h2></Reveal>
            {p.pricing.intro && <Reveal delay={2}><p className="mt-4 max-w-2xl text-base text-ink/60">{p.pricing.intro}</p></Reveal>}
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {p.pricing.levels.map((l, i) => (
                <Reveal key={l.name} delay={i}>
                  <div className={`h-full rounded-2xl border p-8 ${i === 1 ? "border-lime-dim/50 bg-white shadow-lg" : "border-ink/10 bg-white"}`} data-testid={`cp-pricing-${l.name.toLowerCase()}`}>
                    <p className="font-serif text-2xl text-ink">{l.name}</p>
                    <p className="mt-3 font-serif text-3xl text-lime-dim">{l.price}</p>
                    <p className="mt-3 text-sm leading-relaxed text-ink/60">{l.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={2}>
              <p className="mt-6 max-w-2xl text-sm text-ink/50">Repères indicatifs issus de nos formats habituels. Votre devis détaille chaque poste : lieu, traiteur, technique, personnel, animation, logistique.</p>
            </Reveal>
          </div>
        </section>
      ) : (
      <section className="border-t border-ink/10 bg-mist" data-testid="cp-budget">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Budget indicatif</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">Des repères pour cadrer</h2></Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {p.budget.map((b, i) => (
              <Reveal key={b.label} delay={i}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-8">
                  <p className="text-sm text-ink/55">{b.label}</p>
                  <p className="mt-3 font-serif text-3xl text-ink">{b.range}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}>
            <p className="mt-6 max-w-2xl text-sm text-ink/50">Fourchettes indicatives issues de nos formats habituels, hors options exceptionnelles. Votre devis détaille chaque poste : lieu, traiteur, technique, personnel, animation, logistique.</p>
          </Reveal>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-white" data-testid="cp-faq">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Questions fréquentes</span></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {p.faq.map((f, i) => (
              <Reveal key={f.q} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7">
                  <h3 className="font-serif text-xl leading-snug text-ink">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage interne — pages liées */}
      {p.related && (
        <section className="border-t border-ink/10 bg-white" data-testid="cp-related">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
            <Reveal><span className={overline}>À découvrir aussi</span></Reveal>
            <div className="mt-6 flex flex-wrap gap-3">
              {p.related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-mist px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`cp-related-${r.to.replace(/\//g, "")}`}>
                  {r.label} <ArrowUpRight size={14} className="text-lime-dim transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA final */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28" data-testid="cp-cta">
        <Reveal><h2 className="max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-6xl">Recevez une première <span className="italic text-lime-dim">proposition</span>.</h2></Reveal>
        <Reveal delay={1}><p className="mt-5 max-w-xl text-lg text-ink/60">Brief en 2 minutes. Réponse sous 24 h ouvrées. Sans engagement.</p></Reveal>
        <Reveal delay={2}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="cp-final-devis-btn">Recevoir une première proposition <ArrowRight size={16} /></Link>
            <button onClick={() => { track("click_rappel"); setCallbackOpen(true); }} className={btnOutline} data-testid="cp-final-callback-btn">Être rappelé</button>
          </div>
        </Reveal>
      </section>
    </>
  );
}
